import { motion } from "framer-motion";
import { Heart, Sparkle, Fish } from "@phosphor-icons/react";

const THEMES = [
  {
    icon: Heart,
    title: "Empathy & Kindness",
    text: "Thomas the whale learns that true help comes from those who care — not just those who watch.",
  },
  {
    icon: Sparkle,
    title: "Being Appreciated",
    text: "A story about being valued for who you are, beyond what others see on the surface.",
  },
  {
    icon: Fish,
    title: "Unexpected Friendship",
    text: "When Thomas meets Edvin the blind shark and Dominic the dolphin, he discovers the magic of true friends.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: "easeOut" },
  }),
};

export default function BookSection() {
  return (
    <section
      id="book"
      data-testid="book-section"
      className="relative py-24 md:py-32"
    >
      <div className="section-separator mb-24 md:mb-32" />

      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          custom={0}
        >
          <p
            className="text-sm tracking-widest uppercase mb-4"
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 400,
              color: "var(--text-secondary)",
              letterSpacing: "0.15em",
            }}
          >
            The Story
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl mb-6"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 400,
              color: "var(--text-primary)",
              lineHeight: 1.2,
            }}
          >
            About <span style={{ color: "var(--gold)" }}>The Disco Whale</span>
          </h2>
          <div className="gold-divider mb-10" />
        </motion.div>

        {/* Book description */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          custom={1}
          className="max-w-2xl mb-20"
        >
          <p
            className="text-base md:text-lg leading-relaxed mb-6"
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 300,
              color: "var(--text-secondary)",
              lineHeight: 1.85,
            }}
          >
            The whale Thomas has gotten stuck in a string of colourful lamps by accident.
            He is sad because it is difficult for him to swim with the string of lamps stuck
            in his fins and tail, and none of the fish seem to care about Thomas; they only
            think it is fun that the colourful lamps light up in the ocean.
          </p>
          <p
            className="text-base md:text-lg leading-relaxed"
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 300,
              color: "var(--text-secondary)",
              lineHeight: 1.85,
            }}
          >
            One day Thomas meets the shark Edvin. Edvin can no longer see, but wants to
            help Thomas get free from the lamp string. Thomas and Edvin become friends.
            Together they continue swimming in the ocean and meet the dolphin Dominic.
            Perhaps Dominic can help Thomas?
          </p>
        </motion.div>

        {/* Theme cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {THEMES.map((theme, i) => (
            <motion.div
              key={theme.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              custom={i + 2}
              data-testid={`theme-card-${i}`}
              className="p-8 md:p-10 rounded-sm transition-all duration-300"
              style={{
                backgroundColor: "var(--bg-surface)",
                border: "1px solid rgba(234,208,161,0.08)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(234,208,161,0.2)";
                e.currentTarget.style.transform = "translateY(-4px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(234,208,161,0.08)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <theme.icon
                size={28}
                weight="light"
                style={{ color: "var(--gold)", marginBottom: "1.25rem" }}
              />
              <h3
                className="text-lg mb-3"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 500,
                  color: "var(--text-primary)",
                }}
              >
                {theme.title}
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 300,
                  color: "var(--text-secondary)",
                  lineHeight: 1.7,
                }}
              >
                {theme.text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
